import React, { useEffect, useState, type ChangeEvent } from "react";

import { Constants } from "@eagerdog/constants";
import { ScentScoreDto, IScore } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Checkbox from "src/components/Checkbox/Checkbox";
import StopWatch from "src/components/EnterResults/StopWatch/StopWatch";

import "./CWagsScentResults.scss";

interface IProps {
  result: any,
  onChange(result: ScentScoreDto): void
}

const resultTypeOptions: IOption[] = [
  { id: Constants.cwags_scent_result.pass, value: Constants.cwags_scent_result.pass },
  { id: Constants.cwags_scent_result.fail, value: Constants.cwags_scent_result.fail },
  { id: Constants.cwags_scent_result.abs, value: Constants.cwags_scent_result.abs },
];

const faultsOptions: IOption[] = [
  {id: "None", value: "None"},
  {id: "Dropped Food", value: "Dropped Food"},
  {id: "Dog stops working", value: "Dog stops working"},
  {id: "Handler guiding dog", value: "Handler guiding dog"},
  {id: "Incorrect find", value: "Incorrect find"},
  {id: "Destructive behavior", value: "Destructive behavior"},
  {id: "Disturbing search area by dog or handler", value: "Disturbing search area by dog or handler"},
  {id: "Verbally naming item", value: "Verbally naming item"},
  {id: "Continue search after \"alert\"", value: "Continue search after \"alert\""},
  {id: "SR crossing line less than half", value: "SR crossing line less than half"}
];

const CWagsScentResults: React.FC<IProps> = ({ result, onChange }) => {
  const _result = result.scent;

  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const [resultType, setResultType] = useState<IOption>(() => {
    if (_result?.result_type === Constants.cwags_scent_result.pass) {
      return resultTypeOptions[0]; // Pass
    } else if (_result?.result_type === Constants.cwags_scent_result.abs) {
      return resultTypeOptions[2]; // ABS
    } else if (_result?.result_type === Constants.cwags_scent_result.fail) {
      return resultTypeOptions[1]; // Fail
    }
    // Fallback for legacy data stored as qualified/absent/not_qualified
    if (_result?.result_type === Constants.score_result_type.qualified) {
      return resultTypeOptions[0]; // Pass
    } else if (_result?.result_type === Constants.score_result_type.absent) {
      return resultTypeOptions[2]; // ABS
    } else {
      return resultTypeOptions[1]; // Fail
    }
  });

  const [scent1, setScent1] = useState<boolean>(_result?.scent1 ?? true);
  const [scent2, setScent2] = useState<boolean>(_result?.scent2 ?? true);
  const [scent3, setScent3] = useState<boolean>(_result?.scent3 ?? true);
  const [scent4, setScent4] = useState<boolean>(_result?.scent4 ?? true);

  const [fault1, setFault1] = useState<IOption>(faultsOptions.find((o) => o.id === _result?.fault1) ?? faultsOptions[0]);
  const [fault2, setFault2] = useState<IOption>(faultsOptions.find((o) => o.id === _result?.fault2) ?? faultsOptions[0]);

  const [time, setTime] = useState<string>(_result?.search_time ?? "");

  useEffect(() => {
    if (hasChanged) {
      onChange({
        result_type: resultType.id, // Store the actual C-WAGS value: "Pass", "Fail", or "ABS"
        scent1,
        scent2,
        scent3,
        scent4,
        fault1: fault1.id,
        fault2: fault2.id,
        search_time: time,
      });

      setHasChanged(false);
    }
  }, [hasChanged, onChange,fault1.id, fault2.id, resultType.id, scent1, scent2, scent3, scent4, time]);

  const handleResultTypeChange = (_e: ChangeEvent<HTMLInputElement>, value: IOption) => {
    setResultType(value);
    setHasChanged(true);
  }

  const handleTimeChange = (value: string) => {
    setTime(value);
    setHasChanged(true);
  }

  return (
    <div className="CWagsScentResults">
      <Dropdown value={resultType} label="Result" onChange={handleResultTypeChange} options={resultTypeOptions} placeholder="" />
      <p>Select Scents</p>
      <div className="scentsWrap">
        <Checkbox id="scent-1-cb" label="Scent 1" value={scent1} onChange={() => { setScent1(!scent1); setHasChanged(true); }} />
        <Checkbox id="scent-2-cb" label="Scent 2" value={scent2} onChange={() => { setScent2(!scent2); setHasChanged(true); }} />
        <Checkbox id="scent-3-cb" label="Scent 3" value={scent3} onChange={() => { setScent3(!scent3); setHasChanged(true); }} />
        <Checkbox id="scent-4-cb" label="Scent 4" value={scent4} onChange={() => { setScent4(!scent4); setHasChanged(true); }} />
      </div>
      <Dropdown value={fault1} label="Fault 1" onChange={(_e, value: IOption) => { setFault1(value); setHasChanged(true); }} options={faultsOptions} placeholder="" />
      <Dropdown value={fault2} label="Fault 2" onChange={(_e, value: IOption) => { setFault2(value); setHasChanged(true); }} options={faultsOptions} placeholder="" />
      <StopWatch label="Time" time={time} onChange={handleTimeChange} />
    </div>
  )
}

export default CWagsScentResults
